import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'src/auth';
import { getValidatedFromLocalStorage, setJSONInLocalStorage } from 'src/utils/safeStorage';
import { z } from 'zod';

import type { PaletteCardData } from '../components/palette/ResultCard';
import { transientSlotFields } from '../constants/collections';

/**
 * The palette's empty-query "Recent" list — real records this user opened from
 * the palette, never invented.
 *
 *   open a result ─▶ prepend · dedupe by route · cap 6 ─▶ localStorage
 *   malformed blob ──╳──▶ no recents (degrades, never throws)
 *
 * Stores the composed `PaletteCardData` (minus functions), so a recent renders
 * through the same `ResultCard` as a live hit and a projection or route change
 * can't retro-break an old entry.
 *
 * SECURITY (TAS-3542): the store key is the (user, org) identity `SearchProvider`
 * scopes on, so recents never survive sign-out or an org switch — a shared
 * workstation's next identity can't read the prior one's customer names. Read
 * drops any row whose collection left the caller's entitlement; write persists
 * ONLY the projected fields, never the raw engine document — and drops any
 * slot the registry marks `transient` (text blobs: `notesText` / `lineItemsText`
 * / … never touch localStorage). Highlight snippets are dropped too: a recent
 * has no live match, so it renders its stored slots unhighlighted.
 */

const KEY_PREFIX = 'agriful.globalSearch.recents';
const MAX_RECENTS = 6;

const slotSchema = z.object({ field: z.string(), label: z.string(), value: z.string() });

const recentSchema = z.object({
  id: z.string(),
  accentKey: z.string(),
  identifier: z.string().optional(),
  typeLabel: z.string(),
  status: z.string().optional(),
  slots: z.array(slotSchema).catch([]),
  route: z.string(),
  collectionKey: z.string(),
});

const recentsSchema = z.array(recentSchema).catch([]);

/**
 * The exact, safe fields we persist — nothing off the raw document rides along.
 * Transient slots (text blobs) are stripped by collection, and the live-only
 * highlight segments are dropped so a stored slot is `{ field, label, value }`.
 */
function project(card: PaletteCardData): PaletteCardData {
  const transient = transientSlotFields(card.collectionKey);
  return {
    id: card.id,
    accentKey: card.accentKey,
    identifier: card.identifier,
    typeLabel: card.typeLabel,
    status: card.status,
    slots: card.slots
      .filter((slot) => !transient.has(slot.field))
      .map((slot) => ({ field: slot.field, label: slot.label, value: slot.value })),
    route: card.route,
    collectionKey: card.collectionKey,
  };
}

function storageKeyFor(identity: string | null): string | null {
  return identity ? `${KEY_PREFIX}.${identity}` : null;
}

function loadRaw(storageKey: string | null): PaletteCardData[] {
  if (typeof window === 'undefined' || !storageKey) return [];
  return getValidatedFromLocalStorage(storageKey, recentsSchema) ?? [];
}

export interface RecentRecordsState {
  recents: PaletteCardData[];
  /** Record an opened result at the top of the list (deduped by route). */
  record: (card: PaletteCardData) => void;
}

export function useRecentRecords(entitledCollections: string[]): RecentRecordsState {
  const { currentUser } = useAuth();

  // Scope on the SAME identity the search key is minted for, so recents re-key on
  // login and org-switch and never bleed across identities on a shared machine.
  const storageKey = useMemo(
    () => storageKeyFor(currentUser ? `${currentUser.id}:${currentUser.organizationId ?? ''}` : null),
    [currentUser]
  );

  // The persisted history for THIS identity, reloaded only when identity changes.
  const [stored, setStored] = useState<PaletteCardData[]>(() => loadRaw(storageKey));
  useEffect(() => {
    setStored(loadRaw(storageKey));
  }, [storageKey]);

  // Primitive key so the entitlement filter is a pure derive (no effect, no loop
  // from the array-prop identity changing every render). Collection aliases never
  // contain a comma, so a joined string round-trips losslessly.
  const entitledKey = [...entitledCollections].sort().join(',');
  const recents = useMemo(() => {
    // Drop anything the caller is no longer entitled to search — never let a previous entitlement's text reach the DOM.
    const entitled = new Set(entitledKey ? entitledKey.split(',') : []);
    return stored.filter((card) => entitled.has(card.collectionKey)).slice(0, MAX_RECENTS);
  }, [stored, entitledKey]);

  const record = useCallback(
    (card: PaletteCardData) => {
      if (!storageKey) return;
      // Derive `next` from committed state and write OUTSIDE the setState updater:
      // React treats updaters as pure and may double-invoke them (StrictMode), so a
      // localStorage write inside one would run twice per `record()`.
      const projected = project(card);
      const next = [projected, ...stored.filter((entry) => entry.route !== projected.route)].slice(0, MAX_RECENTS);
      setStored(next);
      setJSONInLocalStorage(storageKey, next);
    },
    [storageKey, stored]
  );

  return { recents, record };
}
